import { useCallback, useEffect, useMemo, useState } from 'react';

const KEY = 'lc_devotional_reads';
const todayKey = () => new Date().toLocaleDateString('en-CA');

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
};

/**
 * Device-local reading progress for the weekly devotional.
 * reads: { "<slug>:<day_number>": "YYYY-MM-DD" }  (date the day was read)
 */
export const useReadingStreak = (slug, dayCount) => {
  const [reads, setReads] = useState(load);

  useEffect(() => {
    const sync = () => setReads(load());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const markRead = useCallback((dayNumber) => {
    const id = `${slug}:${dayNumber}`;
    setReads((prev) => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: todayKey() };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, [slug]);

  const isRead = useCallback((dayNumber) => Boolean(reads[`${slug}:${dayNumber}`]), [reads, slug]);

  const stats = useMemo(() => {
    const thisWeek = Object.keys(reads).filter((k) => k.startsWith(`${slug}:`)).length;
    // consecutive calendar days (ending today or yesterday) with at least one read
    const dates = new Set(Object.values(reads));
    let streak = 0;
    const cursor = new Date();
    if (!dates.has(cursor.toLocaleDateString('en-CA'))) cursor.setDate(cursor.getDate() - 1);
    while (dates.has(cursor.toLocaleDateString('en-CA'))) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
    return { thisWeek, streak, total: Object.keys(reads).length, dayCount };
  }, [reads, slug, dayCount]);

  return { markRead, isRead, stats };
};
